
import ActionDialogWorkCenter from '@/components/common/ActionDialogWorkCenter.vue'
import TrackedDataFlowMarumiya from '@/components/detail/TrackedDataFlowMarumiya.vue'
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue'
import DetailAddressBox from '@/components/DetailAddressBox.vue'
import DetailPageButtons from '@/components/DetailPageButtons.vue'
import KeyenceHTHardKeySerialNumberButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeySerialNumberButton.vue'
import BarcodeKeyValueInfoMixin from '@/components/mixins/BarcodeKeyValueInfoMixin.vue'
import BarcodeTypesMixin from '@/components/mixins/BarcodeTypesMixin.vue'
import ManufacturingDateCheckMixin from '@/components/mixins/ManufacturingDateCheckMixin.vue'
import TrackingPointNameBox from '@/components/TrackingPointNameBox.vue'
import {
  BARCODE_ACTION_COMPLETE,
  CLEAR_ERROR,
  CLEAR_LOADING,
  FETCH_BARCODE,
  NUMBER_BARCODE_ACTION_COMPLETE,
  SAVE_SCANNED_EXTERNAL_ID,
  SAVE_SCANNED_WORK_CENTER_CODE,
  SET_LOADING,
  SET_PAGE_NOT_FOUND,
} from '@/store/actions'
import { generateBarcode, updateReferenceFieldBarcode } from '@/utils/api'
import { DATE_TIME } from '@/utils/constants'
import { EBCRType, ECameraReaderType, ECustomFieldType, EFileKind } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { objectToArray, openMessage } from '@/utils/helpers'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { IBarcode } from '@/utils/types/barcodeTypes'
import { TError } from '@/utils/types/commonTypes'
import { ICustomFieldData } from '@/utils/types/fieldData'
import { IFieldSettings, IFormInputSchema } from '@/utils/types/fieldDefinition'
import { IProject } from '@/utils/types/projectTypes'
import { format } from 'date-fns'
import isEmpty from 'lodash/isEmpty'
import { mixins, Options } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { NavigationGuardNext, RouteLocation, RouteLocationNormalized } from 'vue-router'

@Options({
  name: 'DetailView',
  components: {
    DetailAddressBox,
    TrackingPointNameBox,
    TrackedDataFlowMarumiya,
    DetailPageButtons,
    ActionDialogWorkCenter,
    KeyenceHTHardKeySerialNumberButton,
    ConfirmDialog,
  },
  beforeRouteUpdate(to: RouteLocation, from: RouteLocation) {
    if (to.params.barcodeId === from.params.barcodeId) return

    this.loading = true
    this.$store
      .dispatch(FETCH_BARCODE, {
        id: to.params.barcodeId,
        path: to.query?.path ?? '',
        trackingNumber: to.query?.trackingNumber ?? '',
        externalId: to.query?.externalId ?? '',
      })
      .catch((error: TError) => {
        errorHandler(error)
      })
      .finally(() => {
        this.loading = false
      })
  },
})
export default class DetailView extends mixins(
  BarcodeTypesMixin,
  BarcodeKeyValueInfoMixin,
  ManufacturingDateCheckMixin
) {
  loading = true
  isShowDialog = false
  ocrAction = ECameraReaderType.BCR

  get isKeyenceEnterpriseBrowser() {
    return isKeyenceEnterpriseBrowser()
  }

  get allowActivateBarcode() {
    return this.$store.getters.projectPermission?.activationDataPermission?.canEdit || this.isClientUser || false
  }

  getFilesArrFromCustomFields(isImageFileKind: boolean) {
    const customFields = this.barcode?.activationData?.customFields || {}

    const fileType = ECustomFieldType.FILES
    const referenceType = ECustomFieldType.REFERENCE
    const activationCustomFields = this.barcodeTypeInfo?.activationFields?.customFields

    return Object.keys(customFields).reduce((acc, currKey) => {
      const field = customFields[currKey]
      const isFiles = field.fieldType === fileType
      const isReference = field.fieldType === referenceType

      if (!isFiles && !isReference) return acc

      const matchesKind = !isFiles || isImageFileKind === this.isFileKindImage(currKey)
      if (!matchesKind) return acc

      const fieldLabel = activationCustomFields?.[currKey]?.label
      return { ...acc, [currKey]: { ...field, label: fieldLabel } }
    }, {} as Record<string, ICustomFieldData>)
  }

  confirmText(value: string) {
    this.isShowDialog = false
    this.$store.dispatch(SAVE_SCANNED_WORK_CENTER_CODE, value)
  }

  get hasTrackPoints(): boolean {
    const currentNode = this.barcode?.currentTrackPointKey
    if (!currentNode) return false

    const trackPoints = this.project?.trackPoints
    if (!trackPoints) return false

    const currentTrackPoint = trackPoints[currentNode]
    if (!currentTrackPoint) return false

    const nextTrackPointKeys = Object.keys(currentTrackPoint.trackPointForms || {})
    const permissions = this.$store.getters.projectPermission
    const isWritable = permissions?.trackingPermissions?.[currentNode]?.canEdit

    return isWritable && nextTrackPointKeys.some(
      (key) => permissions?.nextTrackingPointPermissions?.[key]?.canEdit
    )
  }

  handleWorkCenterButtonClick() {
    if (!this.hasTrackPoints) {
      this.navigateToHomePage()
    } else {
      this.openWorkCenterScan()
    }
  }

  openWorkCenterScan() {
    this.isShowDialog = true
    this.ocrAction = ECameraReaderType.BCR
  }

  navigateToHomePage() {
    this.$router.push({ name: 'home' })
  }

  isFileKindImage(key: string) {
    const customFieldsSetting = this.barcodeTypeInfo?.activationFields?.customFields || {}
    if (customFieldsSetting?.[key]?.fieldType === ECustomFieldType.FILES) {
      return [EFileKind.BARCODE_SCAN_FRAME, EFileKind.IMAGE, EFileKind.PHOTO_ONLY].find((imageKey) => {
        return customFieldsSetting?.[key]?.fileKind === imageKey
      })
    }
    return false
  }

  get ESignArrayObj() {
    const activationDataCF = this.barcode?.activationData?.customFields || {}
    const activationSettingCF: Record<string, IFormInputSchema> = this.barcodeTypeInfo?.activationFields?.customFields

    if (!activationDataCF || !activationSettingCF) return []

    return Object.keys(activationDataCF as IFormInputSchema).reduce((acc, currKey) => {
      const fieldData = activationDataCF[currKey]
      if (fieldData?.fieldType === ECustomFieldType.ESIGN) {
        acc.push({
          fieldType: ECustomFieldType.ESIGN,
          label: activationSettingCF[currKey]?.label,
          eSign: fieldData.eSign,
        })
      }
      return acc
    }, [] as ICustomFieldData[])
  }

  get isAuth(): boolean {
    return this.$store.getters.isAuth
  }

  get customBarcodeReaderType() {
    return EBCRType.UNKNOWN
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get currentTrackPoint(): string {
    return this.project?.trackPoints?.[this.currentTrackPointKey]?.name || '---'
  }

  get trackingPoints() {
    return {
      start: this.startTrackPoint || '',
      current: this.currentTrackPoint || '',
    }
  }

  get packageInfoAvailable() {
    return (
      this.isTrackingNumberAvailable ||
      this.isImageDataAvailable ||
      this.isExternalIdAvailable ||
      this.isDimensionAvailable ||
      this.isActivationDataCustomFieldsAvailable
    )
  }

  get isActivationDataCustomFieldsAvailable() {
    const isCustomFieldsAvailable = objectToArray<IFieldSettings>(
      this.flattenFields(this.barcodeTypeInfo?.activationFields)
    ).some((val) => val?.value?.isAvailable)
    return isCustomFieldsAvailable || false
  }

  get isShowPackageInfo() {
    const maskedFields = this.maskedFields
    const packageInfoMaskedFields =
      maskedFields?.filter((val) => {
        if (val !== 'origin' && val !== 'destination') return val
      }) || []
    return (
      !isEmpty(this.barcode?.activationData?.dimension) ||
      !isEmpty(this.barcode?.activationData?.trackingNumber) ||
      !isEmpty(this.dimension) ||
      packageInfoMaskedFields?.length > 0
    )
  }

  get barcode(): IBarcode {
    return this.$store.state.barcode?.barcode
  }

  get manufacturingPlan() {
    return this.barcode?.manufacturingPlan
  }

  get manufactureCompletionDate() {
    return this.manufacturingPlan?.manufactureCompletionDate
      ? format(new Date(this.manufacturingPlan.manufactureCompletionDate), DATE_TIME.WITH_SECOND)
      : ''
  }

  get actionCompleted() {
    return this.$store.state.barcode.actionCompleted
  }

  get projectParam() {
    return this.$store.getters.projectParam
  }

  get maskedFields() {
    return this.barcode?.activationData?.maskedFields || []
  }

  get getImagesKeyArr() {
    const activationCustomField = this.barcodeTypeInfo?.activationFields?.customFields || {}
    if (!activationCustomField) return []

    return Object.keys(activationCustomField as IFormInputSchema).reduce((acc, currKey) => {
      const field = activationCustomField[currKey]
      if (field?.fieldType === ECustomFieldType.FILES && field?.fileKind === EFileKind.IMAGE) {
        acc.push(currKey)
      }
      return acc
    }, [] as string[])
  }

  get isMaskedImage() {
    const fileArray = this.getImagesKeyArr || []
    const isMaskedImage = fileArray.some((key) => this?.maskedFields?.includes(`customFields.${key}`))
    return isMaskedImage || false
  }

  get isMaskedEsign() {
    const fileArray = this.getEsignsKeyArr || []
    const isMaskedEsignField = fileArray.some((key) => this?.maskedFields?.includes(`customFields.${key}`))
    return isMaskedEsignField || false
  }

  get getEsignsKeyArr() {
    const activationCustomField = this.barcodeTypeInfo?.activationFields?.customFields
    if (!activationCustomField) return []

    return Object.keys(activationCustomField as IFormInputSchema).reduce((acc, currKey) => {
      if (activationCustomField[currKey]?.fieldType === ECustomFieldType.ESIGN) {
        acc.push(currKey)
      }
      return acc
    }, [] as string[])
  }

  async onRefreshReferenceFieldClick() {
    this.loading = true
    try {
      await updateReferenceFieldBarcode(this.barcode.id)
      await this.$store.dispatch(FETCH_BARCODE, {
        id: this.$route.params.barcodeId,
        trackingNumber: this.trackingNumberFromPath,
        externalId: this.externalIdFromPath,
        path: this.queryPath,
      })
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }

  get allowToEdit(): boolean {
    return !this.barcode.isDeactivated && isEmpty(this.barcode?.trackingData) && this.isAuth
  }

  get startTrackPoint() {
    // Take startTrackPointKeyOverride of barcode for start if it exist
    const startOverride = this.barcode?.startTrackPointKeyOverride
    const project = this.$store.state.project.details

    return startOverride ? project?.trackPoints[startOverride].name : project?.startTrackPoint?.name
  }

  get hasCustomHTML(): boolean {
    const hasCustomContentTemplates = !!this.barcodeTypeInfo?.customContentTemplates

    const keys: string[] = this.barcodeTypeInfo?.customContentTemplates?.[0]?.trackPointKeys ?? []

    return hasCustomContentTemplates && keys.includes(this.currentTrackPointKey) && !this.isAuth
  }

  get trackingNumberFromPath() {
    return this.$route.query?.trackingNumber ?? ''
  }

  get externalIdFromPath() {
    return this.$route.query?.externalId ?? ''
  }

  get isGenerateBarcode() {
    return this.$route.query?.isGenerateBarcode && this.$route.query.isGenerateBarcode === '1'
  }

  get queryPath() {
    return this.$route.query?.path ?? ''
  }

  beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    const restrictBackRoutes = [
      'shipping-packageInfo',
      'shipping-origin',
      'shipping-destination',
      'activate-trackpoint',
      'activate-type',
    ]
    const name = to.name as string

    const isAllowToBack =
      !(this.barcode.isActivated && restrictBackRoutes.includes(name)) &&
      !(this.barcode.isDeactivated && name === 'track')

    if (!isAllowToBack) {
      openMessage(this.$t('errors.3095'), 'error')
    }

    next(isAllowToBack)
  }

  async mounted() {
    const shouldOpenWorkCenter = this.$route.query?.openWorkCenter === 'true'
    try {
      await this.$store.dispatch(FETCH_BARCODE, {
        id: this.$route.params.barcodeId,
        trackingNumber: this.trackingNumberFromPath,
        externalId: this.externalIdFromPath,
        path: this.queryPath,
      })

      if (this.project && this.barcode) {
        // Reload to match the project code and barcodeId
        this.$router.replace({
          name: 'detail',
          params: { project: this.$store.getters.projectParam, barcodeId: this.barcode?.id },
        })
      }

      // validate barcodeType and isActivated barcode
      const isBarcodeInvalid = (isEmpty(this.barcodeTypeInfo) && this.barcode?.isActivated) || !this.barcode
      if (isBarcodeInvalid) {
        this.$store.dispatch(SET_PAGE_NOT_FOUND, { item: 'barcode' })
        this.loading = false
      }

      if (this.checkManufacturingDate()) {
        return
      }

      // forward to custom content if have
      if (this.hasCustomHTML) {
        this.moveToCustomHtmlPage()
        return
      }

      if (shouldOpenWorkCenter && !isBarcodeInvalid) this.openWorkCenterScan()

      // notify action completed
      if (!this.actionCompleted) return
      this.onActionCompleted(this.actionCompleted)
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.loading = false
    }
  }

  moveToCustomHtmlPage() {
    if (!this.hasCustomHTML) return
    this.$router.push({ name: 'custom-html' })
  }

  @Watch('$store.state.barcode.error')
  async onBarcodeErrorUpdate(error: boolean) {
    if (error) {
      this.$store.dispatch(SET_PAGE_NOT_FOUND, { item: 'barcode' })
      this.loading = false
      if (this.externalIdFromPath) {
        this.showBarcodeNotFoundConfirmation()
      }
    } else {
      window.scrollTo(0, 0)
    }
  }

  showBarcodeNotFoundConfirmation() {
    if (!this.isGenerateBarcode) return

    this.$confirm(this.$t('generate_barcode_not_exist'), this.$t('Warning'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
    })
      .then(() => this.hanleConfirmGenerateNewBarcode())
      .catch(() => {
        this.$store.commit(CLEAR_ERROR)
        this.$router.push({ name: 'home' })
      })
  }

  async hanleConfirmGenerateNewBarcode() {
    this.$store.dispatch(SET_LOADING)
    try {
      this.$store.dispatch(SAVE_SCANNED_EXTERNAL_ID, this.externalIdFromPath)
      const newBarcode = await this.generateNewBarcode()

      await this.$store.dispatch(FETCH_BARCODE, {
        id: newBarcode.id,
        trackingNumber: this.trackingNumberFromPath,
        externalId: this.externalIdFromPath,
        path: this.queryPath,
      })

      this.$router.push({
        name: 'activate-type',
        params: {
          project: this.$store.getters.projectCode,
          barcodeId: newBarcode.id,
        },
      })
    } catch (e) {
      errorHandler(e as TError)
    } finally {
      this.$store.dispatch(CLEAR_LOADING)
    }
  }

  async generateNewBarcode() {
    this.loading = true
    try {
      return await generateBarcode(this.$store.getters.projectCode, `${this.$store.getters.projectVersion}`)
    } catch (error) {
      errorHandler(error as TError)
      throw error
    } finally {
      this.loading = false
    }
  }

  @Watch('$store.state.barcode.actionCompleted')
  onActionCompleted(action: string) {
    if (!action) return

    const numberOfBCAffected = this.$store.state.barcode.numberOfBCAffected
    const barcode = numberOfBCAffected === 0 || numberOfBCAffected === 1 ? 'barcode' : 'barcodes'

    openMessage(this.$t(`${barcode} ${action}`, { numberOfBCAffected }), 'success')

    this.$store.dispatch(NUMBER_BARCODE_ACTION_COMPLETE, 0)
    this.$store.dispatch(BARCODE_ACTION_COMPLETE, '')
  }

  get projectCode() {
    return this.project?.code
  }
}
